import { useEffect, useMemo, useState } from "react";
import { useFetcher, useLocation } from "react-router-dom";

import Modal, { ModalContent, ModalSections } from "components/Modal";
import { PricingOptionCard } from "components/pricingoptions/PricingOptionCard";
import { PayAsYouGoContent } from "components/pricingoptions/PayAsYouGoContent";
import { AutoUpgradeContent } from "components/pricingoptions/AutoUpgradeContent";
import { AnnualResubscribeContent } from "components/pricingoptions/AnnualResubscribeContent";
import SubmitButton from "components/SubmitButton";

import { useActiveSubscription } from "routes/data-routes/billing-data/active-subscription";
import { useToasterFromActionData } from "hooks/useToasterFromActionData";
import { useCallbackAfterSubmission } from "v2/hooks/useCallbackAfterSubmission";
import { usePricingOptions } from "routes/data-routes/billing-data/pricing-options";
import { usePricingOptionsWarnings } from "v2/hooks/billing/pricing-options/usePricingOptionsWarnings";

import { useUser } from "routes/dataroutes/UserData";

import { isLegacyAnnualPlan, isYearlyPlan } from "utils/planUtils";


function AutoRenewalCard(
  {
    closeModal,
  }: {
    closeModal: () => void;
  }
) {
  const user = useUser();

  const fetcher = useFetcher();

  useCallbackAfterSubmission(closeModal, fetcher);

  return (
    <PricingOptionCard
      title={ "Auto-renewal" + (user?.planAutoRenewal ? (" (on, " + user.planAutoRenewal + "%)") : "") }
      body={ "You " + (user?.planAutoRenewal ? "will" : "can") + " continue renewing your plan until December 2025, after which this feature will no longer be available" }
      features={ [ "Same plan features" ] } // TODO should we read these from plan internals and list the individual features?
      footer={
        !user?.planAutoRenewal &&
          <fetcher.Form
            method="POST"
            action="/billing-data/subscription/auto-renew-100"
          >
            <SubmitButton
              text="Enable Auto-Renewal"
              className="button button-secondary mt-5 w-full"
              size="MD"
              fetchersToCheck={ [ fetcher ] }
              disabled={ fetcher.state === "loading" && !!fetcher.formData }
            />
          </fetcher.Form>
      }
    />
  );
}


export function PricingOptionsWarningPopupModal() {
  const { activeSubscription } = useActiveSubscription();
  const { pricingOptions, pricingOptionsFetcher, reloadPricingOptions } = usePricingOptions();
  const { consumptionMilestone, isWarningMilestoneDismissed, dismissWarningMilestone } = usePricingOptionsWarnings();
  const location = useLocation();
  // do not pop up if flag was set in state, or if going to a sub-route of /billing directly without inner navigation
  const [ forceHideModal ] = useState<boolean>((location.state?.pricingOptionsWarning === false) || (location.pathname.startsWith("/billing/")));

  const [ dontShowAgain, setDontShowAgain ] = useState<boolean>(false);

  const showModal = useMemo(() => {
    if (
      (pricingOptionsFetcher.state === "idle") && // prevent closing the dialog immediately after clicking a button
      (pricingOptions?.["pay-as-you-go"]?.enabled || pricingOptions?.["auto-upgrade"]?.enabled)
    ) {
      // don't show if any of the new features is already enabled
      return false;
    }

    if (!activeSubscription) {
      // do not show if the user does not have an active subscription
      return false;
    }

    // for legacy annual plans (with monthly credits), we don't offer anything here
    if (isLegacyAnnualPlan(activeSubscription?.planSlug)) {
      return false;
    }

    return isWarningMilestoneDismissed === false;
  }, [ activeSubscription, isWarningMilestoneDismissed, pricingOptions, pricingOptionsFetcher.state ]);

  const [ show, setShow ] = useState(forceHideModal ? false : showModal);
  useEffect(() => setShow(forceHideModal ? false : showModal), [ showModal, forceHideModal ]);

  useToasterFromActionData(pricingOptionsFetcher);


  return (
      <Modal
        show={ show }
        headline={ "You have consumed " + consumptionMilestone + "% of your credits" }
        className="dark"
        onClose={ () => {
          if (dontShowAgain) {
            dismissWarningMilestone();
          }
          setShow(false);
          reloadPricingOptions();
        } }
      >
        <ModalContent>
          <ModalSections>
            <p>Avoid service interruptions when you exceed your plan limits.</p>
            <div className="flex flex-row gap-x-4 min-w-[600px]">
              { (!isLegacyAnnualPlan(activeSubscription?.planSlug) && !isYearlyPlan(activeSubscription?.planSlug)) && (
                // standard monthly plans
                <>
                  { pricingOptions?.["auto-upgrade"]?.allowed && <AutoUpgradeContent recommended /> }

                  { pricingOptions?.["pay-as-you-go"]?.allowed && <PayAsYouGoContent recommended /> }

                  { pricingOptions?.renewal?.allowed && <AutoRenewalCard closeModal={ () => setShow(false) }/> }
                </>
              ) }

              { isYearlyPlan(activeSubscription?.planSlug) && (
                // new annual plans with yearly credits
                <>
                  <AnnualResubscribeContent recommended />

                  <AutoUpgradeContent primary={ false } />
                </>
              ) }
            </div>

            <div className="flex flex-row justify-end">
              <label className="flex flex-row items-center gap-x-2">
                <input
                  type="checkbox"
                  className="dark:rounded dark:focus:ring-0"
                  name="pricing-option-dont-show-again"
                  checked={ dontShowAgain }
                  onChange={ (event) => setDontShowAgain(event.currentTarget.checked) }
                />
                <span className="select-none">Don't show this to me again</span>
              </label>
            </div>
          </ModalSections>
        </ModalContent>

      </Modal>
  );
}
